import { Controller } from "@hotwired/stimulus"

// Filter panel with checkboxes for search (replaces SearchFilter.jsx + SearchFilterView.jsx + SearchStore.js)
// Values: query, activeFilters (Object), availableFilters (Object)
// Targets: panel, overlay, selectedFilters, filterSections, updateBtn, filterCount
export default class extends Controller {
  static values = { query: String, activeFilters: Object, availableFilters: Object }
  static targets = ["panel", "overlay", "selectedFilters", "filterSections", "updateBtn", "filterCount"]

  connect() {
    // selectedFilters tracks the currently toggled filters (deep copy of activeFilters)
    this.selectedFilters = JSON.parse(JSON.stringify(this.activeFiltersValue || {}))
    // availableFilters tracks available filter items with active state
    this.availableFilters = JSON.parse(JSON.stringify(this.availableFiltersValue || {}))
    // Set initial active state on available filter items based on activeFilters
    this.syncActiveState()
    this.renderSelectedFilters()
    this.renderFilterSections()
    this.updateButtonState()
    this.updateFilterCount()
  }

  syncActiveState() {
    for (const [filterName, items] of Object.entries(this.availableFilters)) {
      const activeValues = this.selectedFilters[filterName] || []
      items.forEach(item => {
        item.active = activeValues.includes(item.name)
      })
    }
  }

  show() {
    this.panelTarget.classList.remove("-is-hidden")
    if (this.hasOverlayTarget) this.overlayTarget.classList.remove("-is-hidden")
    document.body.style.overflow = "hidden"
  }

  hide() {
    this.panelTarget.classList.add("-is-hidden")
    if (this.hasOverlayTarget) this.overlayTarget.classList.add("-is-hidden")
    document.body.style.overflow = ""
  }

  selectFilter(event) {
    const filterName = event.target.dataset.filterName
    const filterValue = event.target.dataset.filterValue
    const checked = event.target.checked

    if (checked) {
      if (!this.selectedFilters[filterName]) {
        this.selectedFilters[filterName] = []
      }
      this.selectedFilters[filterName].push(filterValue)
    } else {
      if (this.selectedFilters[filterName]) {
        this.selectedFilters[filterName] = this.selectedFilters[filterName].filter(v => v !== filterValue)
        if (this.selectedFilters[filterName].length === 0) {
          delete this.selectedFilters[filterName]
        }
      }
    }

    // Update active state on the available filter item
    const items = this.availableFilters[filterName]
    if (items) {
      const item = items.find(i => i.name === filterValue)
      if (item) item.active = checked
    }

    // Only update the count badge for this section — don't re-render the whole DOM
    // which would collapse the open section
    this.updateSectionCount(filterName)
    this.updateButtonState()
    this.updateFilterCount()
  }

  deleteSelectedFilter(event) {
    event.preventDefault()
    const filterName = event.target.closest("[data-filter-name]")?.dataset.filterName
    if (!filterName) return

    delete this.selectedFilters[filterName]

    // Uncheck all items in this filter
    const items = this.availableFilters[filterName]
    if (items) {
      items.forEach(item => item.active = false)
    }

    this.performSearch()
  }

  resetFilters(event) {
    if (event) event.preventDefault()
    this.selectedFilters = {}
    for (const items of Object.values(this.availableFilters)) {
      items.forEach(item => item.active = false)
    }
    this.performSearch()
  }

  performSearch() {
    const queryString = new URLSearchParams()

    if (this.queryValue) {
      queryString.append("q", this.queryValue)
    }

    for (const [name, values] of Object.entries(this.selectedFilters)) {
      const marker = values.length > 1 ? "[]" : ""
      values.forEach(val => queryString.append(name + marker, val))
    }

    Turbo.visit("/search" + (queryString.toString() !== "" ? "?" : "") + queryString.toString())
    this.hide()
  }

  toggleSection(event) {
    const section = event.target.closest("[data-section]")
    if (!section) return

    const itemList = section.querySelector("[data-section-items]")
    const arrow = section.querySelector("[data-section-arrow]")

    if (itemList.style.display === "none" || itemList.style.display === "") {
      itemList.style.display = "block"
      if (arrow) arrow.innerHTML = `<use href="/icons/__remixicon.sprite.svg?v=491#arrow-down-s-line"/>`
      event.target.closest(".filter-header-row")?.classList.add("-is-active")
    } else {
      itemList.style.display = "none"
      if (arrow) arrow.innerHTML = `<use href="/icons/__remixicon.sprite.svg?v=491#arrow-right-s-line"/>`
      event.target.closest(".filter-header-row")?.classList.remove("-is-active")
    }
  }

  renderSelectedFilters() {
    const entries = Object.entries(this.selectedFilters)
    this.selectedFiltersTarget.innerHTML = entries.map(([name, values]) =>
      `<label class="tag-fill" data-filter-name="${this.escapeHtml(name)}">
        ${this.escapeHtml(name)}: ${values.sort().join(", ")} <a data-action="click->search-filter#deleteSelectedFilter" data-filter-name="${this.escapeHtml(name)}" class="tag-close">&times;</a>
      </label>`
    ).join("")
  }

  renderFilterSections() {
    this.filterSectionsTarget.innerHTML = Object.entries(this.availableFilters).map(([name, items]) => {
      const activeCount = items.filter(item => item.active).length
      const displayName = name === "type" ? "Type" : name

      return `<section class="filter-section" data-section>
        <header class="filter-header-row" data-action="click->search-filter#toggleSection">
          ${this.escapeHtml(displayName)} <div class="filter-header-meta">${activeCount > 0 ? activeCount : ""}</div> <svg data-section-arrow class="icon icon-gray" aria-label=""><use href="/icons/__remixicon.sprite.svg?v=491#arrow-right-s-line"/></svg>
        </header>
        <div class="filter-item-list" data-section-items style="display:none">
          ${items.map(item => `<label for="${this.escapeHtml(item.name)}_filter" class="filter-item">
            <input type="checkbox" id="${this.escapeHtml(item.name)}_filter"
              data-action="change->search-filter#selectFilter"
              data-filter-name="${this.escapeHtml(name)}"
              data-filter-value="${this.escapeHtml(item.name)}"
              ${item.active ? "checked" : ""} />
            ${this.escapeHtml(item.name)} <div class="filter-item-meta">${item.count || ""}</div>
          </label>`).join("")}
        </div>
      </section>`
    }).join("")
  }

  updateSectionCount(filterName) {
    const items = this.availableFilters[filterName]
    if (!items) return
    const activeCount = items.filter(item => item.active).length
    // Find the section whose header contains this filter name
    const sections = this.filterSectionsTarget.querySelectorAll("[data-section]")
    for (const section of sections) {
      const meta = section.querySelector(".filter-header-meta")
      if (!meta) continue
      const header = section.querySelector(".filter-header-row")
      if (header && header.textContent.includes(filterName === "type" ? "Type" : filterName)) {
        meta.textContent = activeCount > 0 ? activeCount : ""
        break
      }
    }
  }

  updateButtonState() {
    const changed = JSON.stringify(this.selectedFilters) !== JSON.stringify(this.activeFiltersValue)
    if (this.hasUpdateBtnTarget) {
      this.updateBtnTarget.disabled = !changed
    }
  }

  updateFilterCount() {
    const count = Object.keys(this.selectedFilters).length
    if (this.hasFilterCountTarget) {
      this.filterCountTarget.textContent = count > 0 ? `(${count})` : ""
    }
  }

  escapeHtml(str) {
    if (!str) return ""
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }
}
